import { HttpClient } from '@euler/lib/request';
import {
  ChatContact,
  ChatGroupChatType,
  ChatMessagePayload,
  ChatMessageReadersResult,
  ChatMessageWriteResult,
  ChatRoomActiveMember,
  ChatRoomDetail,
  ChatRoomMemberInput,
  ChatRoomType,
  ChatThreadListResult,
  ChatThreadMessagesResult,
  ChatUnreadCounts,
  CreateChatRoomResult,
  InviteRoomMembersResult,
  OrderInvolvedMember,
  ResolveChatThreadQuery,
  ResolveChatThreadResult,
} from '@euler/model/chat';
import { IdType } from '@euler/model/common';

export interface ListChatThreadsOptions {
  roomType?: ChatRoomType | ChatRoomType[];
  groupChatType?: ChatGroupChatType | ChatGroupChatType[];
  unread?: boolean;
  includeArchived?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ListChatThreadMessagesOptions {
  before?: string;
  limit?: number;
}

export class ChatService {
  constructor(private readonly api: HttpClient) {}

  async listContacts(options: { name?: string } = {}): Promise<ChatContact[]> {
    return await this.api
      .get()
      .url('/chat/contacts', { name: options.name })
      .future();
  }

  async listThreads(
    options: ListChatThreadsOptions = {},
  ): Promise<ChatThreadListResult> {
    return await this.api
      .get()
      .url('/chat/threads', {
        roomType: serializeArray(options.roomType),
        groupChatType: serializeArray(options.groupChatType),
        unread: options.unread ? 1 : undefined,
        includeArchived: options.includeArchived ? 1 : undefined,
        cursor: options.cursor,
        limit: options.limit,
      })
      .future();
  }

  // Resolve a conversation entry (room id / peer / org member) into the
  // viewer's existing thread. `thread` is null when there's no room yet (open
  // a bare chat); `peer` carries the resolved counterpart for the `member`
  // kind. Viewer identity is attached by the API layer, same as other reads.
  async resolveThread(
    query: ResolveChatThreadQuery,
  ): Promise<ResolveChatThreadResult> {
    return await this.api
      .post()
      .url('/chat/threads/resolve')
      .data(query)
      .future();
  }

  async listThreadMessages(
    threadId: string,
    options: ListChatThreadMessagesOptions = {},
  ): Promise<ChatThreadMessagesResult> {
    return await this.api
      .get()
      .url('/chat/threads/:id/messages', {
        id: threadId,
        before: options.before,
        limit: options.limit,
      })
      .future();
  }

  async getUnread(): Promise<ChatUnreadCounts> {
    return await this.api.get().url('/chat/unread').future();
  }

  async markThreadRead(
    threadId: string,
    upToMessageId?: string,
  ): Promise<{ changed: boolean }> {
    return await this.api
      .post()
      .url('/chat/threads/:id/read', { id: threadId })
      .data({ upToMessageId })
      .future();
  }

  async pinThread(threadId: string, on: boolean): Promise<{ ok: true }> {
    return await this.api
      .post()
      .url('/chat/threads/:id/pin', { id: threadId })
      .data({ on })
      .future();
  }

  async muteThread(threadId: string, on: boolean): Promise<{ ok: true }> {
    return await this.api
      .post()
      .url('/chat/threads/:id/mute', { id: threadId })
      .data({ on })
      .future();
  }

  async archiveThread(threadId: string, on: boolean): Promise<{ ok: true }> {
    return await this.api
      .post()
      .url('/chat/threads/:id/archive', { id: threadId })
      .data({ on })
      .future();
  }

  async deleteThread(threadId: string): Promise<{ ok: true }> {
    return await this.api
      .delete()
      .url('/chat/threads/:id', { id: threadId })
      .future();
  }

  async editMessage(
    messageId: string,
    payload: ChatMessagePayload,
  ): Promise<{ ok: true }> {
    return await this.api
      .patch()
      .url('/chat/messages/:id', { id: messageId })
      .data({ payload })
      .future();
  }

  async deleteMessage(messageId: string): Promise<{ ok: true }> {
    return await this.api
      .delete()
      .url('/chat/messages/:id', { id: messageId })
      .future();
  }

  async toggleReaction(messageId: string, emoji: string): Promise<unknown> {
    return await this.api
      .post()
      .url('/chat/messages/:id/reactions', { id: messageId })
      .data({ emoji })
      .future();
  }

  /** Mark a received audio message played — clears its red unread dot. */
  async markAudioPlayed(
    messageId: string,
  ): Promise<{ messageId: string; audioPlayedAt: string }> {
    return await this.api
      .post()
      .url('/chat/messages/:id/played', { id: messageId })
      .future();
  }

  /** Who has read a message — the "read by" list behind the double-check. */
  async listMessageReaders(
    messageId: string,
  ): Promise<ChatMessageReadersResult> {
    return await this.api
      .get()
      .url('/chat/messages/:id/readers', { id: messageId })
      .future();
  }

  async sendPrivateMessage(body: {
    roomId?: string;
    to?: { platformUserId: IdType; appId: string; appScope: string };
    payload: ChatMessagePayload;
    clientMessageId: string;
    quoteMessageId?: string;
    authorRole?: string | null;
  }): Promise<ChatMessageWriteResult> {
    return await this.api
      .post()
      .url('/chat/messages/private')
      .data(body)
      .future();
  }

  async sendGroupMessage(body: {
    roomId: string;
    payload: ChatMessagePayload;
    clientMessageId: string;
    quoteMessageId?: string;
    authorRole?: string | null;
  }): Promise<ChatMessageWriteResult> {
    return await this.api
      .post()
      .url('/chat/messages/group')
      .data(body)
      .future();
  }

  async getRoomActiveMembers(roomId: string): Promise<ChatRoomActiveMember[]> {
    return await this.api
      .get()
      .url('/chat/rooms/:roomId/active-members', { roomId })
      .future();
  }

  async getRoom(roomId: string): Promise<ChatRoomDetail> {
    return await this.api.get().url('/chat/rooms/:roomId', { roomId }).future();
  }

  /** Self-join an existing group room (same-store staff). */
  async joinRoom(
    roomId: string,
  ): Promise<{ outcome: 'joined' | 'already-member' }> {
    return await this.api
      .post()
      .url('/chat/rooms/:roomId/join', { roomId })
      .future();
  }

  async createRoom(body: {
    groupChatType: ChatGroupChatType;
    title?: string;
    avatarUrl?: string;
    members?: ChatRoomMemberInput[];
  }): Promise<CreateChatRoomResult> {
    return await this.api.post().url('/chat/rooms').data(body).future();
  }

  /** Read-only: does an order already have a live internal room? */
  async resolveOrderRoom(orderNo: string): Promise<{ roomId: string } | null> {
    return await this.api
      .get()
      .url('/chat/rooms/order/:orderNo', { orderNo })
      .future();
  }

  /** The order's involved staff (advisor + technicians) — picker default. */
  async listOrderSuggestedMembers(
    orderNo: string,
  ): Promise<OrderInvolvedMember[]> {
    return await this.api
      .get()
      .url('/chat/rooms/order/:orderNo/suggested-members', { orderNo })
      .future();
  }

  /** Create the order-bound internal room (owner + >=1 picked member). */
  async createOrderGroupChatRoom(body: {
    orderNo: string;
    members: ChatRoomMemberInput[];
  }): Promise<{ roomId: string; created: boolean }> {
    return await this.api.post().url('/chat/rooms/order').data(body).future();
  }

  async updateRoomMetadata(
    roomId: string,
    patch: Record<string, string | null | undefined>,
  ): Promise<{ ok: true }> {
    return await this.api
      .patch()
      .url('/chat/rooms/:roomId', { roomId })
      .data({ patch })
      .future();
  }

  async addRoomMember(
    roomId: string,
    member: ChatRoomMemberInput,
  ): Promise<{ memberRowId: string; reactivated: boolean }> {
    return await this.api
      .post()
      .url('/chat/rooms/:roomId/members', { roomId })
      .data({ member })
      .future();
  }

  async inviteRoomMembers(
    roomId: string,
    members: ChatRoomMemberInput[],
  ): Promise<InviteRoomMembersResult> {
    return await this.api
      .post()
      .url('/chat/rooms/:roomId/invite', { roomId })
      .data({ members })
      .future();
  }

  async leaveRoom(
    roomId: string,
  ): Promise<{ outcome: 'left' | 'already-gone' }> {
    return await this.api
      .delete()
      .url('/chat/rooms/:roomId/members/me', { roomId })
      .future();
  }

  async kickRoomMember(
    roomId: string,
    memberRowId: string,
  ): Promise<{ outcome: 'kicked' | 'already-gone' }> {
    return await this.api
      .delete()
      .url('/chat/rooms/:roomId/members/:memberRowId', {
        roomId,
        memberRowId,
      })
      .future();
  }

  async dismissRoom(roomId: string): Promise<{ ok: true }> {
    return await this.api
      .post()
      .url('/chat/rooms/:roomId/dismiss', { roomId })
      .future();
  }
}

function serializeArray(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v.join(',') : v;
}
